import { Form, TimePicker, Typography, Flex } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import CustomSelect from '../../../../components/CustomSelect.js';
import { getFormattedUserTimeZone } from '../../../../common/utils/utils.js';
import { FrequencyOptions, DayOptions } from '@/common/static_data';

const { Text } = Typography;

interface NotificationFrequencySelectorProps {
  selectedFrequency: string;
  setSelectedFrequency: (value: string) => void;
}

/**
 * Notification frequency selector with conditional time and day inputs.
 *
 * Displays a frequency dropdown (excluding "Real Time") and conditionally renders additional fields:
 * - WEEKLY: Day selector and time picker
 * - ONCE_A_DAY: Single time picker
 * - TWICE_A_DAY: Two time pickers
 *
 * @param selectedFrequency - Current frequency selection (WEEKLY, ONCE_A_DAY, TWICE_A_DAY, or empty)
 * @param setSelectedFrequency - Callback to update frequency selection
 */
const NotificationFrequencySelector = ({
  selectedFrequency,
  setSelectedFrequency,
}: NotificationFrequencySelectorProps) => {
  const getFrequencyOptions = () => {
    return FrequencyOptions.slice(1);
  };

  const TimezoneNote = () => (
    <Text style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
      {getFormattedUserTimeZone()}
    </Text>
  );

  return (
    <>
      <Flex gap={8}>
        <Form.Item
          className='custom-input label-14px'
          label='Frequency'
          name='frequency'
          style={{ flex: 1 }}
          rules={[{ required: true, message: 'Please select frequency' }]}
        >
          <CustomSelect
            placeholder='Select'
            onChange={(value: string) => setSelectedFrequency(value)}
            options={getFrequencyOptions()}
            className='frequency-input'
          />
        </Form.Item>
        {selectedFrequency === 'WEEKLY' && (
          <>
            <Form.Item label='Send On' required style={{ marginBottom: 0 }}>
              <Flex align='center' gap={8}>
                <Form.Item
                  name={'day'}
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Please select a day' }]}
                >
                  <CustomSelect
                    suffixIcon={
                      <DownOutlined
                        style={{ color: 'var(--text-secondary)' }}
                      />
                    }
                    placeholder='Select Day'
                    options={DayOptions}
                    className='frequency-input'
                  />
                </Form.Item>
                <span>at</span>
                <Form.Item
                  name='time_2'
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Please select a time' }]}
                >
                  <TimePicker
                    className='frequency-input'
                    suffixIcon={
                      <DownOutlined
                        style={{
                          color: 'var(--text-secondary)',
                          fontSize: '12px',
                        }}
                      />
                    }
                    use12Hours
                    format='h:mm a'
                  />
                </Form.Item>
              </Flex>
              <TimezoneNote />
            </Form.Item>
          </>
        )}
        {(selectedFrequency === 'ONCE_A_DAY' ||
          selectedFrequency === 'TWICE_A_DAY') && (
          <>
            <Form.Item label='Send at' required style={{ marginBottom: 0 }}>
              <Flex align='center' gap={8}>
                <Form.Item
                  name='time_1'
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Please select a time' }]}
                >
                  <TimePicker
                    className='frequency-input'
                    suffixIcon={
                      <DownOutlined
                        style={{
                          color: 'var(--text-secondary)',
                          fontSize: '12px',
                        }}
                      />
                    }
                    use12Hours
                    format='h:mm a'
                  />
                </Form.Item>
                {selectedFrequency === 'TWICE_A_DAY' && (
                  <>
                    <span>&</span>

                    <Form.Item
                      name='time_2'
                      style={{ flex: 1 }}
                      rules={[
                        { required: true, message: 'Please select a time' },
                      ]}
                    >
                      <TimePicker
                        className='frequency-input'
                        suffixIcon={
                          <DownOutlined
                            style={{
                              color: 'var(--text-secondary)',
                              fontSize: '12px',
                            }}
                          />
                        }
                        use12Hours
                        format='h:mm a'
                      />
                    </Form.Item>
                  </>
                )}
              </Flex>
              <TimezoneNote />
            </Form.Item>
          </>
        )}
      </Flex>
    </>
  );
};

export default NotificationFrequencySelector;
